import cn from 'clsx'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import GeoContext from 'contexts/geoContext'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { appViewMessageHandler } from 'lib/window/webViewUtils'
import { CreateTripItemRequest } from 'tripPlanner/api/tripItem/types'
import MultiTypeItemBookingCard from 'tripPlanner/components/Bookings/MultiTypeItemBookingCard'
import SingleTypeItemBookingCard from 'tripPlanner/components/Bookings/SingleTypeItemBookingCard'
import {
  OfferTypeForTripItemTypes,
  OrderAndOffers,
  useCreateTripItemBatch,
  useOrdersByMemberId,
  useTrip,
} from 'tripPlanner/hooks/api'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import { CLOSE_MODAL } from 'tripPlanner/types/common'
import { orderHasMultiItemTypes } from 'tripPlanner/utils'
import { convertTripItemsFromOrderItems } from 'tripPlanner/utils/itemConversions/tripItemFromOrderItems'
import { getFutureFilteredOrdersNotInTrip } from 'tripPlanner/utils/summary'

interface Props {
  tripId: string
}

const ListOfOrders = styled.ul`
  li {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    border: 1px solid ${({ theme }) => theme.palette.neutral.default.five};
    border-radius: ${(props) => props.theme.borderRadius.S};

    .order-details {
      flex: 1;
    }

    &.isPackage {
      align-items: flex-start;
    }

    &:first-child {
      margin-top: 0;
    }

    &:hover,
    &.selected {
      border: 1px solid ${({ theme }) => theme.palette.brand.primary.normal};
    }

    &:empty {
      display: none;
    }

    & > div:empty {
      display: none;
    }
  }
`

function SelectBookingToAdd(props: Props) {
  const { tripId } = props
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)

  const { memberId, headlessMode } = useAppSelector(
    (state) => ({
      memberId: state.auth.account.memberId,
      headlessMode: state.config.headlessMode,
    }),
    shallowEqual,
  )

  const { data: trip, isLoading: isTripLoading } = useTrip({ tripId })

  const { closeModal, goBack } = useModalCallbacks()

  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [orderAndOffers, setOrderAndOffers] = useState<OrderAndOffers[]>([])

  const { data: orders, isLoading: isOrdersLoading } = useOrdersByMemberId(
    currentRegionCode,
    memberId,
  )

  const closeCurrentModal = useCallback(() => {
    if (headlessMode) {
      appViewMessageHandler('nativeNavigation', CLOSE_MODAL)
    }
    closeModal()
  }, [closeModal, headlessMode])

  const filteredOrders = useMemo(
    () => getFutureFilteredOrdersNotInTrip(orders, trip),
    [orders, trip],
  )

  const { mutate: createMutate, isLoading: isCreateLoading } =
    useCreateTripItemBatch({
      onSuccess: () => {
        closeModal()
      },
      onSettled: () => {
        setIsSaving(false)
      },
    })

  const handleOnSelect = useCallback(
    (
      order: App.Order,
      offers: Array<OfferTypeForTripItemTypes>,
      checked: boolean,
    ) => {
      if (checked) {
        setOrderAndOffers([
          ...orderAndOffers,
          {
            order,
            offers,
          },
        ])
      } else {
        setOrderAndOffers(
          orderAndOffers.filter(
            (orderAndOffer) => orderAndOffer.order.id != order.id,
          ),
        )
      }
    },
    [orderAndOffers],
  )

  const selectedOrderIds = orderAndOffers.map(
    (orderAndOffer) => orderAndOffer.order.id,
  )

  const isLoadingInProgress =
    isCreateLoading || isSaving || isOrdersLoading || isTripLoading

  const handlePrimaryActionClick = useCallback(async () => {
    setIsSaving(true)

    const createItemRequestPromises: Promise<CreateTripItemRequest[]>[] =
      orderAndOffers.map((orderAndOffer) =>
        convertTripItemsFromOrderItems({
          order: orderAndOffer.order,
          offers: orderAndOffer.offers,
          currentRegionCode,
          currentCurrency,
        }),
      )

    const allGroupedItemsPerOder = await Promise.all(createItemRequestPromises)

    const finalCreateTripItemReqsArr: CreateTripItemRequest[] = []
    allGroupedItemsPerOder.map((allGroupedItemsPerOderCreatRequestArr) => {
      finalCreateTripItemReqsArr.push(...allGroupedItemsPerOderCreatRequestArr)
    })

    createMutate({ tripId, tripItems: finalCreateTripItemReqsArr })
  }, [createMutate, currentCurrency, currentRegionCode, orderAndOffers, tripId])

  return (
    <>
      <ModalHeader
        title="Add bookings to trip"
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          {isLoadingInProgress && <LoadingIndicator inline />}
          {!isLoadingInProgress && (
            <ListOfOrders>
              {filteredOrders.map((upcomingOrder: App.Order) => {
                const orderHasMultiItems = orderHasMultiItemTypes(upcomingOrder)

                return (
                  <li
                    key={upcomingOrder.id}
                    className={cn({
                      isPackage: orderHasMultiItems,
                      selected: selectedOrderIds.includes(upcomingOrder.id),
                    })}
                  >
                    <VerticalSpacer gap={24} className="order-details">
                      {orderHasMultiItems && (
                        <MultiTypeItemBookingCard
                          onSelect={handleOnSelect}
                          order={upcomingOrder}
                        />
                      )}
                      {!orderHasMultiItems && (
                        <SingleTypeItemBookingCard
                          onSelect={handleOnSelect}
                          order={upcomingOrder}
                        />
                      )}
                    </VerticalSpacer>
                  </li>
                )
              })}
            </ListOfOrders>
          )}
          {!isLoadingInProgress && filteredOrders.length === 0 && (
            <>
              <Heading variant="heading6">
                All existing bookings added...
              </Heading>
              <BodyText variant="large">
                Any bookings not already in the trip will appear here.
              </BodyText>
            </>
          )}
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Continue',
          disabled: orderAndOffers.length === 0 || isCreateLoading || isSaving,
          onClick: handlePrimaryActionClick,
        }}
        secondaryActionProps={{
          children: 'Cancel',
          onClick: closeCurrentModal,
        }}
      />
    </>
  )
}

export default SelectBookingToAdd
